import { HttpRequestError } from 'viem'
import type { Erc20Transfer, LowerHex } from '../../types'
import type { InternalTransactionWithIndex } from '../../types/internalTransaction'
import type { TransactionIndex } from '../../types/transactionIndex'
import { createInternalTransactionId } from '../../utils/createInternalTransactionId'
import type { Explorer, HeadersInput, LogWithBlockNumber } from '../types'
import {
  getAddressTokenTransferIndexesV2,
  getAddressTransactionIndexesV2,
} from './addressActivityV2'
import { createBlockscoutClient } from './client'
import { getBlockNumberByTimestamp } from './getBlockNumberByTimestamp'
import { getContractCreatorAddressHashAndCreationTransactionHash } from './getContractCreatorAddressHashAndCreationTransactionHash'
import { getContractSourceCodeForAVerifiedContract } from './getContractSourceCodeForAVerifiedContract'
import { getEventLogsByAddressAndTopic } from './getEventLogsByAddressAndTopic'
import { getInternalTransactionsByAddressHash } from './getInternalTransactionsByAddressHash'
import { getInternalTransactionsByAddressHashV2 } from './getInternalTransactionsByAddressHashV2'
import { getInternalTransactionsByTransactionHash } from './getInternalTransactionsByTransactionHash'
import { getTokenTransferEventsByAddress } from './getTokenTransferEventsByAddress'
import { getTransactionsByAddress } from './getTransactionsByAddress'
import { createBlockscoutV2Client } from './v2Client'

/**
 * Resolves headers from either a static object or a function that returns headers.
 * This allows for dynamic header generation (e.g., refreshing auth tokens).
 */
const resolveHeaders = async (
  headers: HeadersInput,
): Promise<Record<string, string> | undefined> => {
  if (headers === undefined) {
    return undefined
  }
  if (typeof headers === 'function') {
    return await headers()
  }
  return headers
}

type CreateBlockscoutParameters = {
  name: string
  baseUrl: string
  apiBaseUrl: string
  proxyUrl?: string
  useAddressInternalTransactionsV2?: boolean
  useAddressApiV2?: boolean
}

export const createBlockscout = ({
  name,
  baseUrl,
  apiBaseUrl,
  proxyUrl,
  useAddressInternalTransactionsV2,
  useAddressApiV2 = false,
}: CreateBlockscoutParameters): Explorer => {
  const client = createBlockscoutClient({ apiBaseUrl, proxyUrl })
  const v2Client = createBlockscoutV2Client({ baseUrl, proxyUrl })

  const legacyExplorer: Explorer = {
    name,
    baseUrl,
    getAddressInternalTransactions: async ({
      address,
      fromBlock,
      toBlock,
      headers,
    }) => {
      const internalTransactions = new Map<
        string,
        InternalTransactionWithIndex
      >()
      let startblock = fromBlock
      const endblock = toBlock
      const maxPageSize = 10_000
      while (true) {
        const resolvedHeaders = await resolveHeaders(headers)
        if (useAddressInternalTransactionsV2) {
          return getInternalTransactionsByAddressHashV2({
            client: v2Client,
            address,
            fromBlock,
            toBlock,
            headers: resolvedHeaders,
          })
        }
        const result = await getInternalTransactionsByAddressHash({
          client,
          address,
          startblock,
          endblock,
          headers: resolvedHeaders,
        })
        for (const transaction of result) {
          const id = createInternalTransactionId(transaction)
          if (!internalTransactions.has(id)) {
            internalTransactions.set(id, transaction)
          }
        }
        if (result.length < maxPageSize) {
          break
        }
        const maxBlockNumber = Math.max(...result.map((x) => x.blockNumber))
        startblock = maxBlockNumber
      }
      return [...internalTransactions.values()]
    },
    getAddressTransactionIndexes: async ({
      address,
      fromBlock,
      toBlock,
      headers,
    }) => {
      const indexes = new Map<LowerHex, TransactionIndex>()
      let startblock = fromBlock
      const endblock = toBlock
      const maxPageSize = 10_000
      while (true) {
        const resolvedHeaders = await resolveHeaders(headers)
        const result = await getTransactionsByAddress({
          client,
          address,
          startblock,
          endblock,
          headers: resolvedHeaders,
        })
        for (const index of result) {
          if (!indexes.has(index.hash)) {
            indexes.set(index.hash, index)
          }
        }
        if (result.length < maxPageSize) {
          break
        }
        const maxBlockNumber = Math.max(...result.map((x) => x.blockNumber))
        startblock = maxBlockNumber
      }
      return [...indexes.values()]
    },
    getAddressTokenTransferIndexes: async ({
      address,
      fromBlock,
      toBlock,
      headers,
    }) => {
      const indexes = new Map<LowerHex, TransactionIndex>()
      const erc20Transfers: Erc20Transfer[] = []
      let startblock = fromBlock
      const endblock = toBlock
      const maxPageSize = 10_000
      while (true) {
        const resolvedHeaders = await resolveHeaders(headers)
        const { indexes: resultIndexes, erc20Transfers: resultErc20Transfers } =
          await getTokenTransferEventsByAddress({
            client,
            address,
            startblock,
            endblock,
            headers: resolvedHeaders,
          })
        for (const index of resultIndexes) {
          if (!indexes.has(index.hash)) {
            indexes.set(index.hash, index)
          }
        }
        erc20Transfers.push(...resultErc20Transfers)
        if (resultIndexes.length < maxPageSize) {
          break
        }
        const maxBlockNumber = Math.max(
          ...resultIndexes.map((x) => x.blockNumber),
        )
        startblock = maxBlockNumber
      }
      return {
        indexes: [...indexes.values()],
        erc20Transfers,
      }
    },
    getBlockNumberOfTimestamp: async ({ timestamp, headers }) => {
      const resolvedHeaders = await resolveHeaders(headers)
      return getBlockNumberByTimestamp({
        client,
        timestamp: Math.floor(timestamp / 1000),
        headers: resolvedHeaders,
      })
    },
    getContract: async ({ address, headers }) => {
      const resolvedHeaders = await resolveHeaders(headers)
      return getContractSourceCodeForAVerifiedContract({
        client,
        address,
        headers: resolvedHeaders,
      })
    },
    getContractCreations: async ({ addresses, headers }) => {
      const resolvedHeaders = await resolveHeaders(headers)
      return getContractCreatorAddressHashAndCreationTransactionHash({
        client,
        contractaddresses: addresses,
        headers: resolvedHeaders,
      })
    },
    getEventLogs: async ({
      address,
      topic0,
      fromBlock: fromBlockParam,
      toBlock: toBlockParam,
      headers,
    }) => {
      const logs: LogWithBlockNumber[] = []
      let fromBlock = fromBlockParam ?? 0
      const toBlock = toBlockParam ?? 'latest'
      const maxPageSize = 10_000
      while (true) {
        const resolvedHeaders = await resolveHeaders(headers)
        const result = await getEventLogsByAddressAndTopic({
          client,
          address,
          topic0,
          fromBlock,
          toBlock,
          headers: resolvedHeaders,
        })
        logs.push(...result)
        if (result.length < maxPageSize) {
          break
        }
        const maxBlockNumber = Math.max(...result.map((x) => x.blockNumber))
        fromBlock = maxBlockNumber
      }
      return logs
    },
    getInternalTransactionOfTransaction: async ({ hash, headers }) => {
      const resolvedHeaders = await resolveHeaders(headers)
      return getInternalTransactionsByTransactionHash({
        client,
        txhash: hash,
        headers: resolvedHeaders,
      })
    },
  }
  if (!useAddressApiV2) return legacyExplorer
  let addressApiV2Available = true
  /** V2 がない古い Explorer のみ、互換 API に戻す。429 や不正な応答は隠さない。 */
  const withAddressApiFallback = async <T>({
    v2,
    legacy,
  }: { v2: () => Promise<T>; legacy: () => Promise<T> }): Promise<T> => {
    if (addressApiV2Available) {
      try {
        return await v2()
      } catch (error) {
        if (
          !(error instanceof HttpRequestError) ||
          ![404, 405].includes(error.status ?? 0)
        )
          throw error
        addressApiV2Available = false
      }
    }
    return legacy()
  }
  return {
    ...legacyExplorer,
    getAddressTransactionIndexes: (params) =>
      withAddressApiFallback({
        v2: () =>
          getAddressTransactionIndexesV2({ ...params, client: v2Client }),
        legacy: () => legacyExplorer.getAddressTransactionIndexes(params),
      }),
    getAddressTokenTransferIndexes: (params) =>
      withAddressApiFallback({
        v2: () =>
          getAddressTokenTransferIndexesV2({ ...params, client: v2Client }),
        legacy: () => legacyExplorer.getAddressTokenTransferIndexes(params),
      }),
    getAddressInternalTransactions: (params) =>
      withAddressApiFallback({
        v2: () =>
          getInternalTransactionsByAddressHashV2({
            ...params,
            client: v2Client,
          }),
        legacy: () => legacyExplorer.getAddressInternalTransactions(params),
      }),
  }
}
